import { Play, Pause, Headphones, Trophy } from 'lucide-react';
import { usePlayer } from '@/context/PlayerContext';
import { LikeButton, ShareButton } from '@/components/actions';

const FALLBACK = 'https://images.pexels.com/photos/167092/pexels-photo-167092.jpeg?auto=compress&cs=tinysrgb&w=600';

export function EntryCard({ entry, queue, onLikeChange }) {
  const { playTrack, current, playing, toggle } = usePlayer();
  const isCurrent = current?.song_id === entry.song_id;
  const track = {
    song_id: entry.song_id, title: entry.title, slug: entry.slug, cover_url: entry.cover_url,
    audio_url: entry.audio_url, style_text: entry.style_text, duration_seconds: entry.duration_seconds,
    creator: entry.creator?.display_name,
  };
  return (
    <div className="card-surface card-hover overflow-hidden" data-testid={`entry-card-${entry.entry_id}`}>
      <div className="relative aspect-video overflow-hidden">
        <img src={entry.cover_url || FALLBACK} alt={entry.title} loading="lazy" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
        <button
          onClick={() => (isCurrent ? toggle() : (entry.song_id && playTrack(track, queue)))}
          data-testid={`entry-play-${entry.entry_id}`}
          className="absolute bottom-3 right-3 z-10 flex h-12 w-12 items-center justify-center rounded-full bg-brand text-black btn-glow transition-transform hover:scale-105"
        >
          {isCurrent && playing ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5 fill-black" />}
        </button>
        <div className="pointer-events-none absolute bottom-3 left-3 right-16">
          <h4 className="truncate font-display text-lg font-bold text-white">{entry.title}</h4>
          <span className="block truncate font-mono text-xs text-brand">{entry.style_text}</span>
        </div>
      </div>
      <div className="flex min-w-0 items-center justify-between gap-2 p-3">
        <div className="flex min-w-0 items-center gap-2">
          <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-surface-cardHover text-xs font-bold text-white">
            {entry.creator?.avatar_url
              ? <img src={entry.creator.avatar_url} alt="" className="h-full w-full object-cover" />
              : (entry.creator?.display_name?.[0] || '?').toUpperCase()}
          </div>
          <span className="truncate text-sm text-muted-foreground">{entry.creator?.display_name || 'Artiste'}</span>
        </div>
        <div className="flex flex-shrink-0 items-center gap-2">
          <span className="flex items-center gap-1 font-mono text-xs text-muted-foreground">
            <Headphones className="h-3.5 w-3.5" />{entry.plays}
          </span>
          <LikeButton entryId={entry.entry_id} likes={entry.likes} liked={entry.liked_by_me}
                      isOwner={entry.is_owner} onChange={onLikeChange} />
          <ShareButton slug={entry.slug} title={entry.title} creator={entry.creator?.display_name} />
        </div>
      </div>
    </div>
  );
}

const MEDALS = ['🥇', '🥈', '🥉'];

export function Leaderboard({ rankings }) {
  if (!rankings?.length) return null;
  return (
    <div className="space-y-2" data-testid="leaderboard">
      {rankings.map((r) => (
        <div key={r.entry_id}
             className={`flex items-center gap-3 rounded-xl border p-3 ${
               r.rank <= 3 ? 'border-brand/40 bg-brand/5' : 'border-border bg-surface-card'}`}>
          <div className="flex h-9 w-9 items-center justify-center font-display text-lg font-bold">
            {r.rank <= 3 ? MEDALS[r.rank - 1] : <span className="text-muted-foreground">{r.rank}</span>}
          </div>
          <img src={r.cover_url || FALLBACK} alt="" className="h-11 w-11 rounded-lg object-cover" />
          <div className="min-w-0 flex-1">
            <p className="truncate font-semibold text-white">{r.title}</p>
            <p className="truncate text-xs text-muted-foreground">{r.creator?.display_name || 'Artiste'}</p>
          </div>
          <div className="text-right">
            <p className="flex items-center gap-1 font-mono font-bold text-brand"><Trophy className="h-3.5 w-3.5" />{r.likes}</p>
            <p className="font-mono text-xs text-muted-foreground">{r.plays} écoutes</p>
          </div>
        </div>
      ))}
    </div>
  );
}
